import DownloadIcon from "@mui/icons-material/Download";
import {
	Box,
	Button,
	CircularProgress,
	Stack,
	TableCell,
	TablePagination,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import InvoiceCard from "../../components/Invoices/InvoiceCard";
import InvoiceStatusChip from "../../components/Invoices/InvoiceStatusChip";
import { EnhancedTable } from "../../components/Table";
import { TableColumn } from "../../components/Table/EnhancedTable";
import { useApi } from "../../contexts/ApiContext";
import { Invoice } from "../../types/subscription";
import formatMoney from "../../utils/formatMoney";
import useInvoiceContext from "../../contexts/InvoiceContext";

function InvoiceTableRow({
	invoice,
	downloadInvoiceLinkMutation: downloadInvoiceLinkMutation,
}: {
	invoice: Invoice;
	downloadInvoiceLinkMutation: any;
}) {
	const { t } = useTranslation();
	return (
		<>
			<TableCell>{invoice.invoiceNumber}</TableCell>
			<TableCell>{dayjs(invoice.issueDate).format("DD.MM.YY")}</TableCell>
			<TableCell>{dayjs(invoice.dueDate).format("DD.MM.YY")}</TableCell>
			<TableCell align="right">
				{formatMoney(invoice.total)}&nbsp;{invoice.currency.symbol}
			</TableCell>
			<TableCell align="center">
				<InvoiceStatusChip status={invoice.status} />
			</TableCell>
			<TableCell align="center" sx={{ textTransform: "capitalize" }}>
				{invoice.paymentMethod}
			</TableCell>

			<TableCell align="center">
				<Button startIcon={<DownloadIcon />} onClick={() => downloadInvoiceLinkMutation.mutate(invoice.id)}>
					{t("invoicePage.downloadPdf")}
				</Button>
			</TableCell>
		</>
	);
}

function InvoicesPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const showCardList = useMediaQuery(theme.breakpoints.down("lg"));
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const { invoices, totalInvoicesCount, isLoadingInvoices } = useInvoiceContext();
	const { invoice: api } = useApi();

	const tableColumns: TableColumn[] = [
		{ id: "Invoice number", label: t("invoicePage.number") },
		{ id: "Issue Date", label: t("invoicePage.issueDate") },
		{ id: "Due Date", label: t("invoicePage.dueDate") },
		{ id: "Total", label: t("invoicePage.total"), align: "right" },
		{ id: "Status", label: t("invoicePage.status"), align: "center" },
		{ id: "Payment Method", label: t("invoicePage.paymentMethod"), align: "center" },
		{ id: "Action", label: t("invoicePage.action"), align: "center" },
	];

	function handleChangePage(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) {
		setPage(newPage);
	}

	function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	}

	const downloadInvoiceLinkMutation = useMutation(
		async (id: string) => {
			const fetchInvoiceLink = await api.downloadInvoiceById(id);
			return fetchInvoiceLink;
		},
		{
			onSuccess(data) {
				if (!data.result) return;
				window.open(data.result.url, "_self");
			},
		}
	);

	if (isLoadingInvoices) {
		return <CircularProgress />;
	}

	if (!invoices || !totalInvoicesCount)
		return (
			<>
				<Typography textAlign={"center"}>{t("invoicePage.noInvoices")}</Typography>
				<Box display={"flex"} justifyContent={"center"}>
					<Button
						variant="contained"
						onClick={() => navigate("/account/subscription/purchase")}
						sx={{ mt: 4, mx: "auto" }}
					>
						{t("subscriptionPage.new")}
					</Button>
				</Box>
			</>
		);

	return (
		<Box display="flex" flexDirection="column" gap={2} data-testid="payment-successful">
			<Typography variant="h5" component="h2">
				{t("invoicePage.pageHeader")}
			</Typography>
			<>
				{showCardList ? (
					<Stack gap={4}>
						{invoices.map((invoice: Invoice) => (
							<InvoiceCard
								invoice={invoice}
								key={invoice.id}
								downloadInvoiceLinkMutation={downloadInvoiceLinkMutation}
							/>
						))}
					</Stack>
				) : (
					<EnhancedTable
						data={invoices}
						columns={tableColumns}
						getExpandedContent={() => <></>}
						getRowContent={(invoice: Invoice) => (
							<InvoiceTableRow
								invoice={invoice}
								downloadInvoiceLinkMutation={downloadInvoiceLinkMutation}
							/>
						)}
						getRowKey={(invoice: Invoice) => invoice.id}
						showExpand={false}
						defaultSortBy="issueDate"
						defaultSortOrder="desc"
					/>
				)}
			</>

			<TablePagination
				count={totalInvoicesCount}
				page={page}
				rowsPerPage={rowsPerPage}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				component="div"
				rowsPerPageOptions={[5, 10, 25]}
			/>
		</Box>
	);
}

export default InvoicesPage;
